import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Layers, CalendarDays, ShieldCheck } from "lucide-react";
import { SectionCard, TextRow, RowsEditor } from "@/components/Brand/editors";

export default function BrandPillarsOpsTab({ brand, onSaved }) {
  const [pillars, setPillars] = useState(brand.pillars || []);
  const [dates, setDates] = useState(brand.important_dates || []);
  const [ops, setOps] = useState(brand.ops || {});
  const [busy, setBusy] = useState(false);
  const setO = (k, v) => setOps((s) => ({ ...s, [k]: v }));

  const pillarSum = pillars.reduce((a, p) => a + (parseInt(p.target_pct, 10) || 0), 0);

  const save = async (payload) => {
    setBusy(true);
    try {
      await api.patch(`/brands/${brand.id}`, payload);
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="brand-pillars-tab">
      <SectionCard title="Content pillars" icon={Layers} onSave={() => save({ pillars })} busy={busy} testid="brand-pillars-section"
        hint={pillarSum !== 100 && pillars.length > 0 ? `⚠ target mix sums to ${pillarSum}% (should be 100%)` : "target mix drives calendar balance warnings"}>
        <RowsEditor
          columns={[
            { key: "name", label: "Pillar" },
            { key: "target_pct", label: "Target mix %", type: "number" },
            { key: "description", label: "Description", type: "textarea" },
          ]}
          rows={pillars} onChange={setPillars} addLabel="Add pillar" testid="pillars" />
      </SectionCard>

      <SectionCard title="Important dates" icon={CalendarDays} onSave={() => save({ important_dates: dates })} busy={busy} testid="brand-dates-section" hint="launches, seasonal campaigns, industry events — will overlay on the content calendar">
        <RowsEditor
          columns={[
            { key: "date", label: "Date", type: "date" },
            { key: "label", label: "Label" },
            { key: "kind", label: "Kind", type: "select", options: ["launch", "seasonal", "event", "other"] },
            { key: "notes", label: "Notes", type: "textarea" },
          ]}
          rows={dates} onChange={setDates} addLabel="Add date" testid="important-dates" />
      </SectionCard>

      <SectionCard title="Ops — approvals & escalation" icon={ShieldCheck} onSave={() => save({ ops })} busy={busy} testid="brand-ops-section">
        <RowsEditor label="Approval contacts"
          columns={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "email", label: "Email" }, { key: "phone", label: "Phone" }]}
          rows={ops.approval_contacts || []} onChange={(v) => setO("approval_contacts", v)} addLabel="Add contact" testid="approval-contacts" />
        <RowsEditor label="Escalation matrix"
          columns={[{ key: "level", label: "Level (L1/L2/L3)" }, { key: "name", label: "Name" }, { key: "contact", label: "Contact" }]}
          rows={ops.escalation || []} onChange={(v) => setO("escalation", v)} addLabel="Add level" testid="escalation" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TextRow label="SLAs" value={ops.slas} onChange={(v) => setO("slas", v)} testid="ops-slas" />
          <TextRow label="Timezone" value={ops.timezone} onChange={(v) => setO("timezone", v)} placeholder="Asia/Kolkata" testid="ops-timezone" />
          <TextRow label="Review windows" value={ops.review_windows} onChange={(v) => setO("review_windows", v)} placeholder="Tue & Thu 3-5pm" testid="ops-review-windows" />
        </div>
      </SectionCard>
    </div>
  );
}
